import React, { useState, useEffect } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import Span from "components/typography/Span";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";
import { lockPrice as lockPriceApi, recordDeposit, createDepositIntent } from "api/QuoteExpiration";
import { getQuote } from "api/Quotes";
import { getSavedPaymentMethods, saveCardFromPayment, saveBankAccountFromPayment } from "api/Payments";
import { useToast } from "components/toast";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCardIcon, BankIcon, CheckMarkIcon } from "components/svg";
import PlaidLinkButton from "components/PlaidLink";
import { exchangePublicToken, createPlaidDepositPayment } from "api/Plaid";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "");

const ACH_FEE = 5;
const CC_FEE_PERCENT = 0.03;

interface PriceLockModalProps {
  quote: any;
  settings?: { priceLockDepositPercent?: number; priceLockMaxDays?: number };
  onComplete: () => void;
  onCancel: () => void;
  initialStep?: "choose" | "pay";
  // N2: when the buying client is flagged requiresDeposit, this modal is
  // the deposit gate at acceptance, not an optional price lock. In that
  // mode we drop the "Pay Later" and early-payment "Pay in Full" options
  // (a deposit client isn't offered the early-pay discount), lead with the
  // deposit requirement, and add the commitment / better-terms nudge.
  depositRequired?: boolean;
}

type PaymentOption = "deposit" | "full" | "later";

// =========================================================================
// Inner CC form (needs to be inside Elements provider)
// =========================================================================
const CCForm: React.FC<{
  clientSecret: string;
  amount: number;
  fees: number;
  quoteId: string;
  quoteNumber: string;
  isFullPayment: boolean;
  clientName: string;
  clientEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ clientSecret, amount, fees, quoteId, quoteNumber, isFullPayment, clientName, clientEmail, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [saveCardChecked, setSaveCardChecked] = useState(false);

  const totalWithFees = +(amount + fees).toFixed(2);
  const fmtCurrency = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: { name: clientName || "Customer", email: clientEmail || undefined },
      },
      setup_future_usage: saveCardChecked ? "off_session" : undefined,
    });

    if (error) {
      toast.add(error.message || "Payment failed", "var(--theme-ui-colors-danger)");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      await recordDeposit(quoteId, {
        method: "stripe_card",
        amount: amount,
        referenceId: paymentIntent.id,
        note: isFullPayment ? "Full payment at lock time via credit card" : "Deposit at lock time via credit card",
      });

      if (saveCardChecked && paymentIntent.id) {
        try {
          await saveCardFromPayment({ paymentIntentId: paymentIntent.id, ownerType: "user" });
          toast.add("Card saved for future payments.", "var(--theme-ui-colors-success)");
        } catch (e: any) { /* don't block */ }
      }

      toast.add("Payment received. Your price is locked.", "var(--theme-ui-colors-success)");
      setSubmitting(false);
      onSuccess();
    } else {
      toast.add("Payment processing failed.", "var(--theme-ui-colors-danger)");
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalBody>
        <P marginTop="0px" marginBottom="20px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
          Your card will be charged a <Span fontWeight="600">{fmtCurrency(fees)}</Span> service fee plus the {isFullPayment ? "full quote amount" : "deposit amount"} for a grand total of <Span fontWeight="600">{fmtCurrency(totalWithFees)}</Span>
        </P>
        <Box sx={{ mb: 3, p: 2, border: "1px solid " + themevals.colors.inputOutline, borderRadius: "6px", background: "#fff" }}>
          <CardElement options={{
            style: {
              base: { fontSize: "14px", fontFamily: "Poppins", color: "#000", backgroundColor: "#fff", "::placeholder": { color: themevals.colors.dkgrey } },
              invalid: { color: themevals.colors.danger },
            },
          }} />
        </Box>
        <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "16px", cursor: "pointer" }} onClick={() => setSaveCardChecked(!saveCardChecked)}>
          <Box sx={{
            width: "18px", height: "18px", borderRadius: "4px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s ease",
            border: "2px solid " + (saveCardChecked ? themevals.colors.primary : themevals.colors.inputOutline),
            backgroundColor: saveCardChecked ? themevals.colors.primary : "#fff",
          }}>
            {saveCardChecked && <CheckMarkIcon fill="#fff" />}
          </Box>
          <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>Save this card for future payments</P>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <BasicButton type="button" onClick={onCancel} scheme="secondaryOutline">Cancel</BasicButton>
        <BasicButton type="button" onClick={handleSubmit} $submitting={submitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
          {!submitting && <CheckMarkIcon fill="#fff" />}Submit Payment
        </BasicButton>
      </ModalFooter>
    </>
  );
};

// =========================================================================
// Main modal
// =========================================================================
const PriceLockModal: React.FC<PriceLockModalProps> = ({ quote: quoteProp, settings, onComplete, onCancel, initialStep, depositRequired }) => {
  const toast = useToast();
  const [step, setStep] = useState<"choose" | "pay">(initialStep || "choose");
  const [submitting, setSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState<PaymentOption>("deposit");
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Fetch full quote data so total is always available regardless of caller
  const [fullQuote, setFullQuote] = useState<any>(null);
  useEffect(() => {
    if (quoteProp?.id) {
      getQuote({ data: { id: quoteProp.id } }).then((res: any) => {
        if (res?.id) setFullQuote(res);
      });
    }
  }, [quoteProp?.id]);
  const quote = fullQuote || quoteProp;

  // Payment state
  const [savedMethods, setSavedMethods] = useState<any[]>([]);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState<"ach" | "card" | null>(null);
  const [ccModalOpen, setCCModalOpen] = useState(false);
  const [achModalOpen, setACHModalOpen] = useState(false);
  const [savedMethodConfirmOpen, setSavedMethodConfirmOpen] = useState(false);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState<any>(null);
  const [savedMethodSubmitting, setSavedMethodSubmitting] = useState(false);

  const quoteTotal = quote.total || quote.subTotal || quote.items?.flat()?.reduce((acc: number, item: any) => acc + (item.price || 0) * (item.quantity || 0), 0) || 0;
  // For a deposit-required client, the percentage is the client's configured
  // depositPercentage (default 50), not the global price-lock default — so
  // the choose-screen figure matches the amount the backend actually locked.
  const depositPercent = depositRequired
    ? (quote?.client?.depositPercentage ?? quote?.primaryClient?.depositPercentage ?? 50)
    : (settings?.priceLockDepositPercent || 10);
  const lockDays = settings?.priceLockMaxDays || 180;
  const depositAmount = Number(quote.priceLockDepositAmount) > 0
    ? Number(quote.priceLockDepositAmount)
    : parseFloat(((quoteTotal * depositPercent) / 100).toFixed(2));
  const fmtCurrency = (v: number) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const depositDeadline = quote.expiresAt ? moment(quote.expiresAt) : null;
  const daysUntilDeadline = depositDeadline ? depositDeadline.diff(moment(), "days") : null;
  const deadlineColor = daysUntilDeadline !== null ? (daysUntilDeadline <= 3 ? "#C53030" : daysUntilDeadline <= 7 ? "#C05621" : "#276749") : "#276749";

  const versionSuffix = (quote.majorVersion > 1 || quote.minorVersion > 0)
    ? (quote.minorVersion > 0 ? "-" + quote.majorVersion + "." + quote.minorVersion : "-" + quote.majorVersion)
    : "";
  const quoteNumber = "GMQ-" + String(quote.sequence).padStart(5, "0") + versionSuffix;
  const paymentAmount = selectedOption === "full" ? quoteTotal : depositAmount;

  const [ccSubmitting, setCCSubmitting] = useState(false);
  const [depositIntent, setDepositIntent] = useState<any>(null);

  // Open CC modal — create deposit intent with card fees first
  const handleOpenCCModal = async () => {
    setCCSubmitting(true);
    const res = await createDepositIntent(quote.id, { paymentMethod: "card" });
    setCCSubmitting(false);
    if (res?.clientSecret) {
      setDepositIntent(res);
      setClientSecret(res.clientSecret);
      setCCModalOpen(true);
    } else {
      toast.add(res?.message || "Failed to create payment intent.", "var(--theme-ui-colors-danger)");
    }
  };

  const ctaText: Record<PaymentOption, string> = {
    deposit: "Lock Price & Pay Deposit",
    full: "Lock Price & Pay in Full",
    later: "Lock Price, Pay Later",
  };

  const fetchSavedMethods = async () => {
    try {
      const methods = await getSavedPaymentMethods();
      setSavedMethods(Array.isArray(methods) ? methods : []);
    } catch (e: any) { console.error("Error fetching saved methods:", e); }
  };

  // Fetch saved methods when entering payment step
  useEffect(() => {
    if (step === "pay") { fetchSavedMethods(); }
  }, [step]);

  const handleConfirm = async () => {
    setSubmitting(true);
    const pctOverride = selectedOption === "full" ? 100 : undefined;
    const res = await lockPriceApi(quote.id, { depositPercent: pctOverride });
    setSubmitting(false);
    if (res?.success) {
      if (selectedOption === "later") {
        toast.add(
          depositRequired
            ? "Deposit set up. Pay online before the deadline."
            : "Price locked. Pay your deposit online before the deadline.",
          "var(--theme-ui-colors-success)",
        );
        onComplete();
      } else {
        toast.add(
          depositRequired
            ? "Deposit set up. Choose your payment method."
            : "Price locked. Choose your payment method.",
          "var(--theme-ui-colors-success)",
        );
        setStep("pay");
      }
    } else {
      toast.add(res?.message || (depositRequired ? "Failed to set up the deposit." : "Failed to lock price."), "var(--theme-ui-colors-danger)");
    }
  };

  // Pay with a saved method — create PI with fees, then confirm with saved PM
  const handleSavedMethodPay = async () => {
    if (!selectedSavedMethod) return;
    setSavedMethodSubmitting(true);

    try {
      const isCard = selectedSavedMethod.methodType === "card";
      const intentRes = await createDepositIntent(quote.id, {
        paymentMethod: isCard ? "card" : "ach",
        stripePaymentMethodId: selectedSavedMethod.stripePaymentMethodId,
      });

      if (!intentRes?.clientSecret) {
        toast.add(intentRes?.message || "Failed to create payment intent.", "var(--theme-ui-colors-danger)");
        setSavedMethodSubmitting(false);
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe not loaded");

      const result = isCard
        ? await stripe.confirmCardPayment(intentRes.clientSecret, {
            payment_method: selectedSavedMethod.stripePaymentMethodId,
          })
        : await stripe.confirmUsBankAccountPayment(intentRes.clientSecret, {
            payment_method: selectedSavedMethod.stripePaymentMethodId,
          });

      if (result.error) {
        toast.add(result.error.message || "Payment failed.", "var(--theme-ui-colors-danger)");
        setSavedMethodSubmitting(false);
        return;
      }

      const piStatus = result.paymentIntent?.status;
      if (piStatus === "succeeded" || piStatus === "processing") {
        const method = isCard ? "stripe_card" : "stripe_ach";
        await recordDeposit(quote.id, {
          method,
          amount: intentRes.baseAmount,
          referenceId: result.paymentIntent.id,
          note: (selectedOption === "full" ? "Full payment" : "Deposit") + " via saved " + (isCard ? "card" : "bank account") + (piStatus === "processing" ? " (ACH processing)" : ""),
        });
        toast.add(
          piStatus === "processing"
            ? "ACH payment submitted. Your price is locked while the payment processes."
            : "Payment received. Your price is locked.",
          "var(--theme-ui-colors-success)"
        );
        setSavedMethodConfirmOpen(false);
        onComplete();
      }
    } catch (err: any) {
      toast.add("Payment error: " + (err?.message || "Unknown error"), "var(--theme-ui-colors-danger)");
    } finally {
      setSavedMethodSubmitting(false);
    }
  };

  // Plaid success handler for new bank account
  const handlePlaidSuccess = async (publicToken: string, metadata: any) => {
    try {
      const exchangeRes = await exchangePublicToken({ publicToken, accountId: metadata.account_id });
      if (exchangeRes?.processorToken) {
        const achRes = await createPlaidDepositPayment({
          processorToken: exchangeRes.processorToken,
          amount: paymentAmount,
          quoteId: quote.id,
          quoteSequence: quote.sequence,
        });

        if (achRes?.success) {
          await recordDeposit(quote.id, {
            method: "plaid_ach",
            amount: paymentAmount,
            referenceId: achRes.paymentIntentId,
            note: (selectedOption === "full" ? "Full payment" : "Deposit") + " via new bank account (Plaid)",
          });

          try {
            await saveBankAccountFromPayment({ bankAccountId: achRes.bankAccountId, ownerType: "user" });
            toast.add("Bank account saved for future payments.", "var(--theme-ui-colors-success)");
          } catch (e: any) { /* don't block */ }

          toast.add("ACH payment submitted. Your price is locked.", "var(--theme-ui-colors-success)");
          setACHModalOpen(false);
          onComplete();
        } else {
          toast.add(achRes?.message || "ACH payment failed.", "var(--theme-ui-colors-danger)");
        }
      } else {
        toast.add("Failed to connect bank account.", "var(--theme-ui-colors-danger)");
      }
    } catch (err: any) {
      toast.add("Error connecting bank: " + (err?.message || "Unknown"), "var(--theme-ui-colors-danger)");
    }
  };

  const formatMethodLabel = (m: any) => {
    if (m.methodType === "card") return (m.brand || "Card") + " ending in " + (m.last4 || "****");
    return (m.bankName || "Bank account") + " ending in " + (m.last4 || "****");
  };

  const optionBorder = (opt: PaymentOption) => selectedOption === opt ? "2px solid #3182CE" : "2px solid #E2E8F0";
  const optionBg = (opt: PaymentOption) => selectedOption === opt ? "#EBF8FF" : (opt === "full" ? "#FAFFF5" : "#fff");

  // =========================================================================
  // SCREEN 1: Lock Terms + Payment Selection
  // =========================================================================
  if (step === "choose") {
    return (
      <Modal isOpen={true} toggle={onCancel} size="md" centered={true}>
        <ModalHeader toggle={onCancel}>
          <Box sx={{ fontFamily: "Rubik", fontSize: "16px", fontWeight: 500, color: themevals.colors.black }}>
            {depositRequired ? "Secure Your Project with a Deposit" : "Lock Quote Price"}
          </Box>
        </ModalHeader>
        <ModalBody>
          <div style={{ fontFamily: "Poppins, sans-serif", overflow: "visible" }}>
            <P fontFamily="Poppins" color={themevals.colors.black} fontSize="14px" fontWeight={400} marginTop="0px" marginBottom="16px">
              {depositRequired
                ? "To get your project scheduled and locked in at this price, a " + depositPercent + "% deposit is required. It goes straight toward your final invoice, so it is money paid on the work, not an extra fee."
                : "Locking this price guarantees the current quote total. Choose how you'd like to proceed."}
            </P>
            <div style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "14px 16px", marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "13px", color: themevals.colors.dkgrey }}>Quote Total</span>
                <span style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>{fmtCurrency(quoteTotal)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "13px", color: themevals.colors.dkgrey }}>{"Deposit (" + depositPercent + "%)"}</span>
                <span style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>{fmtCurrency(depositAmount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", borderTop: "1px solid #E2E8F0", paddingTop: "6px", marginTop: "2px" }}>
                <span style={{ fontSize: "13px", color: themevals.colors.dkgrey }}>Payment Deadline</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: deadlineColor }}>
                  {depositDeadline ? depositDeadline.format("MMM D, YYYY") : "Quote expiration date"}
                  {daysUntilDeadline !== null && (" (" + daysUntilDeadline + "d left)")}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #E2E8F0", paddingTop: "6px", marginTop: "2px" }}>
                <span style={{ fontSize: "13px", color: themevals.colors.dkgrey }}>Lock Duration</span>
                <span style={{ fontSize: "13px", fontWeight: 500, color: themevals.colors.black }}>{lockDays + " days after deposit received"}</span>
              </div>
            </div>
            <P fontFamily="Poppins" color={themevals.colors.black} fontSize="13px" fontWeight={600} marginTop="0px" marginBottom="10px">Payment Option</P>
            {/* Pay deposit */}
            <div onClick={() => setSelectedOption("deposit")} style={{ border: optionBorder("deposit"), backgroundColor: optionBg("deposit"), borderRadius: "8px", padding: "14px 16px", marginBottom: "8px", cursor: "pointer", transition: "border-color 0.15s, background-color 0.15s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ width: "18px", height: "18px", borderRadius: "50%", marginTop: "1px", flexShrink: 0, border: selectedOption === "deposit" ? "5px solid #3182CE" : "2px solid #CBD5E0", backgroundColor: "#fff" }} />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>{"Pay " + depositPercent + "% Deposit"}</div>
                    <div style={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>{"Secures your price for " + lockDays + " days. Balance due when invoiced."}</div>
                  </div>
                </div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#2B6CB0", flexShrink: 0, marginLeft: "16px" }}>{fmtCurrency(depositAmount)}</div>
              </div>
            </div>
            {/* Pay in full — hidden for deposit-required clients (they are
                not offered the early-payment discount). */}
            {!depositRequired && (
            <div onClick={() => setSelectedOption("full")} style={{ border: optionBorder("full"), backgroundColor: optionBg("full"), borderRadius: "8px", padding: "14px 16px", marginBottom: "8px", cursor: "pointer", transition: "border-color 0.15s, background-color 0.15s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ width: "18px", height: "18px", borderRadius: "50%", marginTop: "1px", flexShrink: 0, border: selectedOption === "full" ? "5px solid #3182CE" : "2px solid #CBD5E0", backgroundColor: "#fff" }} />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>
                      Pay in Full
                      <span style={{ fontSize: "10px", fontWeight: 600, color: "#276749", backgroundColor: "#C6F6D5", padding: "2px 6px", borderRadius: "4px", marginLeft: "8px" }}>Early Payment Discount</span>
                    </div>
                    <div style={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>Pay the full amount now. Discount applied when invoice is generated.</div>
                  </div>
                </div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#276749", flexShrink: 0, marginLeft: "16px" }}>{fmtCurrency(quoteTotal)}</div>
              </div>
            </div>
            )}
            {/* Pay later — hidden for deposit-required clients (the deposit
                is required at acceptance, not deferrable). */}
            {!depositRequired && (
            <div onClick={() => setSelectedOption("later")} style={{ border: optionBorder("later"), backgroundColor: selectedOption === "later" ? "#EBF8FF" : "#fff", borderRadius: "8px", padding: "12px 16px", cursor: "pointer", transition: "border-color 0.15s, background-color 0.15s" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ width: "18px", height: "18px", borderRadius: "50%", marginTop: "1px", flexShrink: 0, border: selectedOption === "later" ? "5px solid #3182CE" : "2px solid #CBD5E0", backgroundColor: "#fff" }} />
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 500, color: themevals.colors.dkgrey }}>Pay Later</div>
                  <div style={{ fontSize: "11px", color: "#A0AEC0", marginTop: "2px" }}>{"Lock now, come back and pay online before " + (depositDeadline ? depositDeadline.format("MMM D, YYYY") : "the deadline") + "."}</div>
                </div>
              </div>
            </div>
            )}
            {/* N2 commitment nudge — encouraging, never a promise. */}
            {depositRequired && (
              <div style={{ backgroundColor: "#F0FFF4", border: "1px solid #9AE6B4", borderRadius: "8px", padding: "12px 14px", marginTop: "4px" }}>
                <P fontFamily="Poppins" color="#276749" fontSize="12px" fontWeight={600} marginTop="0px" marginBottom="4px">
                  Building a track record together pays off
                </P>
                <P fontFamily="Poppins" color="#2F855A" fontSize="11px" fontWeight={400} marginTop="0px" marginBottom="0px">
                  {"As we complete more work together and your account is established, we can look at more favorable terms and a reduced or waived deposit on future projects."}
                </P>
              </div>
            )}
            <div style={{ backgroundColor: "#FFFFF0", border: "1px solid #ECC94B", borderRadius: "8px", padding: "10px 14px", marginTop: "16px" }}>
              <P fontFamily="Poppins" color="#744210" fontSize="11px" fontWeight={400} marginTop="0px" marginBottom="0px">
                {selectedOption === "later"
                  ? "Your price locks immediately. Come back and pay your deposit online before the deadline to keep it locked for " + lockDays + " days. If the deadline passes without payment, the lock expires."
                  : selectedOption === "full"
                    ? "Your price locks and you'll proceed to payment. Your early payment discount will be applied when the invoice is generated."
                    : "Your price locks and you'll proceed to payment. The deposit will be credited toward your invoice when the project moves forward."
                }
              </P>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <BasicButton scheme="secondary" outline={true} styles={{ margin: "0 10px .25rem 0" }} type="button" onClick={onCancel}>Cancel</BasicButton>
          <BasicButton scheme="primary" styles={{ margin: "0" }} type="button" onClick={handleConfirm} $submitting={submitting} $loadingColor="#fff" keepText={true}>{ctaText[selectedOption]}</BasicButton>
        </ModalFooter>
      </Modal>
    );
  }

  // =========================================================================
  // SCREEN 2: Payment — matches invoice payment UX
  // =========================================================================
  const savedBanks = savedMethods.filter((m: any) => m.methodType === "us_bank_account");
  const savedCards = savedMethods.filter((m: any) => m.methodType === "card");

  return (
    <>
      <Modal isOpen={true} toggle={onComplete} size="md" centered={true}>
        <ModalHeader toggle={onComplete}>
          <Box sx={{ fontFamily: "Rubik", fontSize: "16px", fontWeight: 500, color: themevals.colors.black }}>
            {selectedOption === "full" ? "Pay in Full" : "Pay Deposit"}
          </Box>
        </ModalHeader>
        <ModalBody>
          <div style={{ fontFamily: "Poppins, sans-serif", position: "relative" }}>
            {/* Locked confirmation */}
            <div style={{ backgroundColor: "#F0FFF4", border: "1px solid #48BB78", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "16px" }}>{"\u2705"}</span>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#276749" }}>
                  {/* N2: a deposit client never chose to lock a price — the
                      lock is the mechanism underneath, not a feature they
                      asked for. Price-lock wording stays in the price-lock
                      flow. */}
                  {depositRequired ? "Quote reserved for you" : "Price locked successfully"}
                </div>
                <div style={{ fontSize: "12px", color: "#285E61" }}>{quoteNumber + " \u2014 " + fmtCurrency(quoteTotal)}</div>
              </div>
            </div>

            {/* Amount due */}
            <div style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "14px", marginBottom: "20px", textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: themevals.colors.dkgrey, marginBottom: "4px" }}>{selectedOption === "full" ? "Total Due" : "Deposit Due"}</div>
              <div style={{ fontSize: "26px", fontWeight: 700, color: selectedOption === "full" ? "#276749" : "#2B6CB0" }}>{fmtCurrency(paymentAmount)}</div>
            </div>

            {/* Payment buttons — matching invoice pattern */}
            <Flex sx={{ justifyContent: "center", gap: "12px" }}>
              {/* Pay via ACH */}
              <Box sx={{ position: "relative" }}>
                <BasicButton scheme="secondaryOutline" type="button" onClick={() => {
                  if (savedBanks.length > 0) { setPaymentDropdownOpen(paymentDropdownOpen === "ach" ? null : "ach"); }
                  else { setACHModalOpen(true); }
                }}>
                  <BankIcon fill={themevals.colors.black} width="14px" height="14px" /> Pay via ACH
                </BasicButton>
                {paymentDropdownOpen === "ach" && (
                  <>
                    <Box onClick={() => setPaymentDropdownOpen(null)} sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} />
                    <Box sx={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid " + themevals.colors.inputOutline, zIndex: 10, minWidth: "320px", overflow: "hidden" }}>
                      {savedBanks.map((method: any) => {
                        const totalWithFee = +(paymentAmount + ACH_FEE).toFixed(2);
                        return (
                          <Flex key={method.id} onClick={() => { setPaymentDropdownOpen(null); setSelectedSavedMethod(method); setSavedMethodConfirmOpen(true); }}
                            sx={{ alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" }, borderBottom: "1px solid " + themevals.colors.inputOutline, gap: "12px" }}>
                            <Flex sx={{ alignItems: "center", gap: "8px", flex: 1 }}>
                              <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                              <Box>
                                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black} style={{ whiteSpace: "nowrap" }}>{method.label || formatMethodLabel(method)}</P>
                                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey} style={{ whiteSpace: "nowrap" }}>
                                  {(method.ownerType === "org" ? "Organization" : "Personal") + " \u00B7 $" + ACH_FEE.toFixed(2) + " fee"}
                                </P>
                              </Box>
                            </Flex>
                            <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.black} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>{fmtCurrency(totalWithFee)}</P>
                          </Flex>
                        );
                      })}
                      <Flex onClick={() => { setPaymentDropdownOpen(null); setACHModalOpen(true); }}
                        sx={{ alignItems: "center", gap: "8px", padding: "10px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" }, borderBottom: "1px solid " + themevals.colors.inputOutline }}>
                        <Box sx={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: themevals.colors.primary, fontSize: "16px", fontWeight: "bold" }}>+</Box>
                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.primary}>Connect a new bank account</P>
                      </Flex>
                      <Flex onClick={() => { setPaymentDropdownOpen(null); window.location.href = "/client-company?tab=payments"; }}
                        sx={{ alignItems: "center", justifyContent: "center", padding: "8px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}>
                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey}>Manage payment methods</P>
                      </Flex>
                    </Box>
                  </>
                )}
              </Box>

              {/* Pay via Credit Card */}
              <Box sx={{ position: "relative" }}>
                <BasicButton scheme="secondaryOutline" type="button" onClick={() => {
                  if (savedCards.length > 0) { setPaymentDropdownOpen(paymentDropdownOpen === "card" ? null : "card"); }
                  else { handleOpenCCModal(); }
                }}>
                  <CreditCardIcon fill={themevals.colors.black} width="14px" height="14px" /> Pay via Credit Card
                </BasicButton>
                {paymentDropdownOpen === "card" && (
                  <>
                    <Box onClick={() => setPaymentDropdownOpen(null)} sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} />
                    <Box sx={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid " + themevals.colors.inputOutline, zIndex: 10, minWidth: "320px", overflow: "hidden" }}>
                      {savedCards.map((method: any) => {
                        const fee = +(paymentAmount * CC_FEE_PERCENT).toFixed(2);
                        const totalWithFee = +(paymentAmount + fee).toFixed(2);
                        return (
                          <Flex key={method.id} onClick={() => { setPaymentDropdownOpen(null); setSelectedSavedMethod(method); setSavedMethodConfirmOpen(true); }}
                            sx={{ alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" }, borderBottom: "1px solid " + themevals.colors.inputOutline, gap: "12px" }}>
                            <Flex sx={{ alignItems: "center", gap: "8px", flex: 1 }}>
                              <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                              <Box>
                                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black} style={{ whiteSpace: "nowrap" }}>{method.label || formatMethodLabel(method)}</P>
                                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey} style={{ whiteSpace: "nowrap" }}>
                                  {(method.ownerType === "org" ? "Organization" : "Personal") + (method.expiryMonth ? " \u00B7 Exp " + String(method.expiryMonth).padStart(2, "0") + "/" + method.expiryYear : "") + " \u00B7 $" + fee.toFixed(2) + " fee"}
                                </P>
                              </Box>
                            </Flex>
                            <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.black} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>{fmtCurrency(totalWithFee)}</P>
                          </Flex>
                        );
                      })}
                      <Flex onClick={() => { setPaymentDropdownOpen(null); handleOpenCCModal(); }}
                        sx={{ alignItems: "center", gap: "8px", padding: "10px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" }, borderBottom: "1px solid " + themevals.colors.inputOutline }}>
                        <Box sx={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: themevals.colors.primary, fontSize: "16px", fontWeight: "bold" }}>+</Box>
                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.primary}>Use a new card</P>
                      </Flex>
                      <Flex onClick={() => { setPaymentDropdownOpen(null); window.location.href = "/client-company?tab=payments"; }}
                        sx={{ alignItems: "center", justifyContent: "center", padding: "8px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}>
                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey}>Manage payment methods</P>
                      </Flex>
                    </Box>
                  </>
                )}
              </Box>
            </Flex>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <P fontFamily="Poppins" color="#A0AEC0" fontSize="11px" fontWeight={400} marginTop="0px" marginBottom="0px">
                {"Or close this window and pay before " + (depositDeadline ? depositDeadline.format("MMM D, YYYY") : "the deadline") + "."}
              </P>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* ─── CC Modal ─── */}
      {ccModalOpen && clientSecret && (
        <Modal isOpen={true} toggle={() => setCCModalOpen(false)} centered closeOnBackdropClick={false}>
          <ModalHeader toggle={() => setCCModalOpen(false)}>
            <ModalIcon><CreditCardIcon fill="000" width="20px" height="20px" /></ModalIcon>
            <ModalTitleWrapper>
              <ModalTitle>Pay via Credit Card</ModalTitle>
              <ModalSubtitle>{selectedOption === "full" ? "Pay the full quote amount to lock your price." : "Pay your deposit to secure your price lock."}</ModalSubtitle>
            </ModalTitleWrapper>
          </ModalHeader>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CCForm
              clientSecret={clientSecret}
              amount={depositIntent?.baseAmount || paymentAmount}
              fees={depositIntent?.fees || 0}
              quoteId={quote.id}
              quoteNumber={quoteNumber}
              isFullPayment={selectedOption === "full"}
              clientName={quote.client?.name || quote.primaryClient?.name || "Customer"}
              clientEmail={quote.client?.billingEmail || quote.primaryClient?.billingEmail || ""}
              onSuccess={() => { setCCModalOpen(false); onComplete(); }}
              onCancel={() => setCCModalOpen(false)}
            />
          </Elements>
        </Modal>
      )}

      {/* ─── ACH Connect Bank Modal ─── */}
      {achModalOpen && (
        <Modal isOpen={true} toggle={() => setACHModalOpen(false)} centered closeOnBackdropClick={false}>
          <ModalHeader toggle={() => setACHModalOpen(false)}>
            <ModalIcon><BankIcon fill="000" width="20px" height="20px" /></ModalIcon>
            <ModalTitleWrapper>
              <ModalTitle>Connect Bank Account</ModalTitle>
              <ModalSubtitle>Connect your bank account securely using Plaid to make an ACH payment.</ModalSubtitle>
            </ModalTitleWrapper>
          </ModalHeader>
          <ModalBody>
            <P marginTop="0px" marginBottom="20px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
              In order to pay via ACH, you must first securely verify your banking credentials via Plaid. Your banking information is never stored on our servers.
            </P>
          </ModalBody>
          <ModalFooter>
            <BasicButton type="button" onClick={() => setACHModalOpen(false)} scheme="secondaryOutline">Cancel</BasicButton>
            <PlaidLinkButton onSuccess={handlePlaidSuccess} buttonText="Connect Your Bank" />
          </ModalFooter>
        </Modal>
      )}

      {/* ─── Confirm Saved Method Payment Modal ─── */}
      {savedMethodConfirmOpen && selectedSavedMethod && (
        <Modal isOpen={true} toggle={() => { setSavedMethodConfirmOpen(false); setSelectedSavedMethod(null); }} centered closeOnBackdropClick={false}>
          <ModalHeader toggle={() => { setSavedMethodConfirmOpen(false); setSelectedSavedMethod(null); }}>
            <ModalIcon>
              {selectedSavedMethod.methodType === "card"
                ? <CreditCardIcon fill="000" width="20px" height="20px" />
                : <BankIcon fill="000" width="20px" height="20px" />
              }
            </ModalIcon>
            <ModalTitleWrapper>
              <ModalTitle>Confirm Payment</ModalTitle>
              <ModalSubtitle>Pay with your saved payment method</ModalSubtitle>
            </ModalTitleWrapper>
          </ModalHeader>
          <ModalBody>
            <Box sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", border: "1px solid " + themevals.colors.inputOutline }}>
              <Flex sx={{ alignItems: "center", gap: "8px" }}>
                {selectedSavedMethod.methodType === "card"
                  ? <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                  : <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                }
                <Box>
                  <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" fontWeight="500" color={themevals.colors.black}>
                    {formatMethodLabel(selectedSavedMethod)}
                  </P>
                  <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="12px" fontWeight="400" color={themevals.colors.dkgrey}>
                    {selectedSavedMethod.ownerType === "org" ? "Organization method" : "Personal method"}
                  </P>
                </Box>
              </Flex>
            </Box>
            {(() => {
              const isCard = selectedSavedMethod.methodType === "card";
              const fee = isCard ? +(paymentAmount * CC_FEE_PERCENT).toFixed(2) : ACH_FEE;
              const totalWithFee = +(paymentAmount + fee).toFixed(2);
              return (
                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                  You are authorizing a payment of <Span fontWeight="600">{fmtCurrency(paymentAmount)}</Span> plus a <Span fontWeight="600">{fmtCurrency(fee)}</Span> service fee for a total of <Span fontWeight="600">{fmtCurrency(totalWithFee)}</Span>.
                  {!isCard && " ACH payments typically take 3-5 business days to settle."}
                </P>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <BasicButton type="button" onClick={() => { setSavedMethodConfirmOpen(false); setSelectedSavedMethod(null); }} scheme="secondaryOutline">Cancel</BasicButton>
            <BasicButton type="button" onClick={handleSavedMethodPay} $submitting={savedMethodSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
              {!savedMethodSubmitting && <CheckMarkIcon fill="#fff" />}Confirm Payment
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default PriceLockModal;
